import {
  isRequired,
  isRequiredIf,
  composeValidators,
  isConditionsAccepted,
  isValidCardNumber,
  isValidCvc,
  isValidDate,
  isValidExpiryDate,
  isValidTaxId,
  isValidZipcode,
  hasLengthGreaterThan,
  onlyContainsValidCharacters,
  isNumeric,
  hasLengthBetween,
  isValidUpiCollectId,
} from '@eti/validation';
import { string } from '@eti/utils';
import { cardFields } from '../payment-methods/card/constants/formFields';
import {
  addressFields,
  nameFields,
  reduxFields,
  PAYMENT_CONDITIONS,
  TAX_DATE_OF_BIRTH,
  TAX_ID,
} from '../constants/formFields';
import type { ValidationTexts } from '../hooks/useValidationTexts';
import { UPI_COLLECT_ID, SELECTED_UPI_OPTION } from '../payment-methods/upi/constants/formFields';
import { APP_CARD_OPTION } from '../payment-methods/app-card/constants/formFields';

const { SELECTED_PAYMENT_METHOD_CODE } = reduxFields;

const validationRules = (
  isTaxIdRequired: boolean,
  { required: message, ...texts }: ValidationTexts,
  taxIdFieldLabel: string,
  taxIdFieldFormat: string,
  allowedCharacters: string,
  validationDisabledCards: string[],
) => {
  const taxIdRequiredMessage =
    taxIdFieldLabel && texts.taxIdRequired
      ? string.insertArgument(texts.taxIdRequired, taxIdFieldLabel)
      : 'tax ID field is Required';
  const invalidTaxIdMessage =
    taxIdFieldLabel && texts.invalidTaxId
      ? string.insertArgument(texts.invalidTaxId, taxIdFieldLabel)
      : 'invalid format for tax ID';

  return {
    [cardFields.CARD_TYPE_ID]: isRequired({ message: texts.cardNumberNotRecognized }),
    [cardFields.CARD_NUMBER]: composeValidators(
      isRequired({ message: texts.cardNumberRequired }),
      hasLengthGreaterThan(5)({ message: texts.cardNumberTooShort }),
      isValidCardNumber(validationDisabledCards)({
        message: texts.invalidCreditCardNumber,
      }),
    )(cardFields.CARD_NUMBER),
    [cardFields.EXPIRE_MONTH_YEAR]: composeValidators(
      isRequired({
        message: texts.expiryDateRequired,
      }),
      isValidExpiryDate({ message: texts.invalidExpiryDate }),
    )(cardFields.EXPIRE_MONTH_YEAR),
    [cardFields.CVC]: composeValidators(
      isRequired({ message: texts.cvvRequired }),
      isValidCvc({ message: texts.invalidCvv }),
    )(cardFields.CVC),
    [cardFields.CARD_HOLDER_FIRST_NAME]: composeValidators(
      isRequired,
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
      hasLengthGreaterThan(1)({ message }),
    )(cardFields.CARD_HOLDER_FIRST_NAME),
    [cardFields.CARD_HOLDER_LAST_NAME]: composeValidators(
      isRequired,
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
      hasLengthGreaterThan(1)({ message }),
    )(cardFields.CARD_HOLDER_LAST_NAME),
    [nameFields.FIRST_NAME]: composeValidators(
      isRequired({ message: texts.firstNameRequired }),
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
      hasLengthGreaterThan(1)({ message: texts.firstNameRequired }),
    )(nameFields.FIRST_NAME),
    [nameFields.LAST_NAME]: composeValidators(
      isRequired({ message: texts.lastNameRequired }),
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
      hasLengthGreaterThan(1)({ message: texts.lastNameRequired }),
    )(nameFields.LAST_NAME),
    [addressFields.STREET]: isRequired({
      message: texts.streetRequired,
    }),
    [addressFields.HOUSE_NUMBER]: isRequired({
      message: texts.houseNumberRequired,
    }),
    [addressFields.ZIP_CODE]: isRequired({
      message: texts.zipCodeRequired,
    }),
    [addressFields.STRICT_ZIP_CODE]: composeValidators(
      isRequired({ message: texts.zipCodeRequired }),
      isValidZipcode(),
    )(addressFields.ZIP_CODE),
    [addressFields.CITY]: isRequired({
      message: texts.cityRequired,
    }),
    [addressFields.COUNTRY]: isRequired({
      message: texts.countryRequired,
    }),
    [addressFields.STATE]: isRequired({
      message: texts.stateOrProvinceRequired,
    }),
    [SELECTED_PAYMENT_METHOD_CODE]: isRequired({ message }),
    [PAYMENT_CONDITIONS]: composeValidators(
      isRequired,
      isConditionsAccepted({ message }),
    )(PAYMENT_CONDITIONS),
    [TAX_DATE_OF_BIRTH]: composeValidators(
      isRequiredIf(() => isTaxIdRequired)({ message: texts.taxDateOfBirthRequired }),
      isValidDate()({ message: texts.invalidTaxDateOfBirth }),
    )(TAX_DATE_OF_BIRTH),
    [TAX_ID]: composeValidators(
      isRequiredIf(() => isTaxIdRequired)({ message: taxIdRequiredMessage }),
      isValidTaxId(taxIdFieldFormat)({ message: invalidTaxIdMessage }),
    )(TAX_ID),
    [cardFields.INICIS_PERSONAL_CARD]: composeValidators(
      isRequired({ message: texts.inicisPersonalCardRequired }),
      isNumeric({ message: texts.inicisInvalidPersonalCard }),
      hasLengthBetween(6, 6)({ message: texts.inicisInvalidPersonalCard }),
    )(cardFields.INICIS_PERSONAL_CARD),
    [cardFields.INICIS_CORPORATE_CARD]: composeValidators(
      isRequired({ message: texts.inicisCorporateCardRequired }),
      isNumeric({ message: texts.inicisInvalidCorporateCard }),
      hasLengthBetween(10, 10)({ message: texts.inicisInvalidCorporateCard }),
    )(cardFields.INICIS_CORPORATE_CARD),
    [SELECTED_UPI_OPTION]: isRequired({
      message,
    }),
    [UPI_COLLECT_ID]: composeValidators(
      isRequired({ message }),
      isValidUpiCollectId()({ message: texts.invalidUpiCollectId }),
    )(UPI_COLLECT_ID),
    [APP_CARD_OPTION]: isRequired({ message }),
  };
};

export default validationRules;
